// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * React hook module for managing item triage lists with filtering capabilities.
 * Provides a convenient interface for fetching and filtering triage item data.
 */
import { getTriageItems } from '@/items/api/triage-get-items';
import { ItemTriageFilter, ItemTriageStatus } from '@/items/api/triage-types';
import { triageItemListQueryKey } from '@/items/triage-query-keys';
import { useSuspenseQuery } from '@tanstack/react-query';
import { z } from 'zod';

/**
 * Configuration options for the useTriageItems hook.
 */
interface UseTriageItemsOptions {
  filter?: ItemTriageFilter;
}

/**
 * React hook for fetching and managing item triage lists with filtering.
 * Uses React Query's suspense mode for data fetching.
 *
 * Features:
 * - Automatic data fetching and caching via React Query
 * - Filtering support via ItemTriageFilter options
 * - Suspense mode for loading states
 *
 * @param options - Configuration object for the hook
 *
 * @returns Object containing:
 * - items: Filtered array of triage items
 * - Query status information (error, refetch, etc.)
 *
 * @throws Will suspend while loading data
 * @throws Will throw errors from the API if request fails
 */
export function useTriageItems(options: UseTriageItemsOptions = {}) {
  const { data, ...query } = useSuspenseQuery({
    queryKey: triageItemListQueryKey(options.filter),
    queryFn: () => getTriageItems(options.filter),
    refetchOnWindowFocus: true,
  });

  return { items: data.items, resultCount: data.totalCount, ...query };
}

/**
 * Search schema for item triage listing page.
 * URL params use lowercase values: 'all', 'matched', 'dismissed', or omitted for needs_review.
 */
export const triageSearchListingSchema = z.object({
  status: z.enum(['all', 'matched', 'dismissed']).optional(),
  page: z.number().optional(),
});

export type TriageSearchListing = z.infer<typeof triageSearchListingSchema>;

/**
 * Converts a URL search parameter status to ItemTriageStatus.
 * - undefined (no param) -> 'NEEDS_REVIEW'
 * - 'all' -> 'all'
 * - 'matched' -> 'MATCHED'
 * - 'dismissed' -> 'DISMISSED'
 */
export function getTriageStatusFromSearch(
  status?: 'all' | 'matched' | 'dismissed'
): ItemTriageStatus {
  if (status === 'all') {
    return 'all';
  }
  if (status === 'matched') {
    return 'MATCHED';
  }
  if (status === 'dismissed') {
    return 'DISMISSED';
  }
  // Default to NEEDS_REVIEW when no status param
  return 'NEEDS_REVIEW';
}
